import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import { useCart } from '../../contexts/CartContext.js';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext.js';
import { jazzcashApi, ordersApi, shippingApi, submitJazzCashRedirect, subscriptionPlanApi } from '../../services/subscriberApi.js';
import { physicalDeliveryChargesPkr } from '../../utils/physicalDelivery.js';
import { toast } from 'react-toastify';

const FLARE_RED = '#c4151c';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const formatPKR = (n) => `Rs ${(Number(n) || 0).toLocaleString('en-PK')}`;

const MCB_ACCOUNT = {
  bank: 'MCB Bank',
  title: 'FLARE',
  number: '0916518001013011',
  iban: 'PK37MUCB0916518001013011',
  /** Bank-app payload decoded from official MCB FLARE QR (not plain IBAN text) */
  qrPayload: '0002020102110202000424PK37MUCB09165180010130111004AA27',
  qrImage: '/payments/mcb-flare-qr.png',
  qrCaption: 'FLARE - 3011',
};

const PAYMENT_METHODS = [
  { id: 'JazzCash', label: 'JazzCash', desc: 'JazzCash wallet / voucher — secure checkout', iconPath: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z', accent: 'from-red-500 to-orange-500', enabled: true },
  { id: 'BankTransfer', label: 'Bank Transfer', desc: 'MCB · Title FLARE — transfer then upload proof', iconPath: 'M3 10h18M5 6h14M5 14a2 2 0 012-2h10a2 2 0 012 2v1a2 2 0 01-2 2H7a2 2 0 01-2-2v-1z', accent: 'from-indigo-500 to-blue-600', enabled: true },
  { id: 'Online', label: 'Credit / Debit Card', desc: 'Visa, Mastercard via JazzCash — 3-D Secure', iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z', accent: 'from-purple-600 to-indigo-700', enabled: true },
  { id: 'EasyPaisa', label: 'EasyPaisa', desc: 'Mobile wallet — instant confirmation', iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z', accent: 'from-green-500 to-emerald-600', enabled: false },
];

const isJazzCashGateway = (method) => method === 'JazzCash' || method === 'Online';

async function redirectToJazzCash(orderNumbers) {
  try {
    const r = await jazzcashApi.prepareRedirect(orderNumbers);
    const body = r?.data || {};
    if (!body.jazzcash?.post_url || !body.jazzcash?.fields) {
      const err = new Error(body.message || 'Could not start JazzCash checkout.');
      err.orderNumbers = orderNumbers;
      throw err;
    }
    toast.info('Redirecting to JazzCash…');
    // POST leaves this page. Checkout must not navigate to thank-you for unpaid JazzCash.
    submitJazzCashRedirect(body.jazzcash);
  } catch (err) {
    if (err?.response?.data?.already_paid && err?.response?.data?.order_number) {
      err.alreadyPaid = true;
      err.orderNumber = err.response.data.order_number;
    }
    if (!err.orderNumbers) err.orderNumbers = orderNumbers;
    throw err;
  }
}

function buildCheckoutFormData(fields, file) {
  const fd = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    fd.append(key, String(value));
  });
  if (file) fd.append('payment_screenshot', file);
  return fd;
}

function McbTransferCard({ amount }) {
  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.info(value);
    }
  };

  return (
    <div className="mt-5 p-5 rounded-2xl border border-gray-200 bg-gradient-to-br from-slate-50 to-white">
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-500">Transfer to</div>
          <div className="mt-2 text-xl font-black text-gray-900 tracking-tight">{MCB_ACCOUNT.title}</div>
          <div className="mt-1 text-sm font-bold text-gray-700">{MCB_ACCOUNT.bank}</div>
          <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">Account number</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <code className="font-mono text-base sm:text-lg font-black text-gray-900 tracking-wider break-all">
              {MCB_ACCOUNT.number}
            </code>
            <button
              type="button"
              onClick={() => copyText(MCB_ACCOUNT.number, 'Account number')}
              className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white text-[10px] font-black uppercase tracking-wider text-gray-700 hover:bg-gray-50"
            >
              Copy
            </button>
          </div>
          <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">IBAN</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <code className="font-mono text-sm sm:text-base font-black text-gray-900 tracking-wide break-all">
              {MCB_ACCOUNT.iban}
            </code>
            <button
              type="button"
              onClick={() => copyText(MCB_ACCOUNT.iban, 'IBAN')}
              className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white text-[10px] font-black uppercase tracking-wider text-gray-700 hover:bg-gray-50"
            >
              Copy
            </button>
          </div>
          <div className="mt-3 text-sm text-gray-600">
            Amount to transfer:{' '}
            <span className="font-black" style={{ color: FLARE_RED }}>{formatPKR(amount)}</span>
          </div>
        </div>
        <div className="shrink-0 rounded-xl border border-gray-200 bg-white px-3 py-3 text-center">
          <img
            src={MCB_ACCOUNT.qrImage}
            alt={`${MCB_ACCOUNT.qrCaption} MCB IBAN QR`}
            className="mx-auto block w-[168px] h-auto"
            width={168}
            height={184}
          />
          <div className="mt-1.5 text-[9px] font-bold uppercase tracking-widest text-gray-500">
            Scan in bank app
          </div>
        </div>
      </div>
      <p className="mt-4 text-xs text-gray-500 leading-relaxed">
        Official MCB QR ({MCB_ACCOUNT.qrCaption}). Scan it in your bank app, or enter the IBAN manually, then add your transfer reference ID below.
      </p>
    </div>
  );
}

const CHECKOUT_DRAFT_KEY = 'flare_checkout_draft_v1';

export default function CheckoutSection() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useCustomerAuth();
  const planId = searchParams.get('plan');
  const {
    items, clearCart,
    hasPhysical, subtotal, unitPrice, lineTotal,
  } = useCart();

  useEffect(() => {
    const jc = searchParams.get('jazzcash');
    if (jc === 'failed') {
      toast.error('JazzCash payment was not completed. Your cart is still here — you can try again. No need to re-add magazines.');
      const next = new URLSearchParams(searchParams);
      next.delete('jazzcash');
      setSearchParams(next, { replace: true });
    } else if (jc === 'ok') {
      toast.success('Payment received. Thank you!');
      clearCart();
      const next = new URLSearchParams(searchParams);
      next.delete('jazzcash');
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(Boolean(planId));
  const [planError, setPlanError] = useState('');
  const [startIssues, setStartIssues] = useState([]);
  const [startMagazineIssueId, setStartMagazineIssueId] = useState('');
  const [shippingCities, setShippingCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesError, setCitiesError] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [cityMenuOpen, setCityMenuOpen] = useState(false);

  useEffect(() => {
    if (!planId) {
      setPlan(null);
      setPlanLoading(false);
      setPlanError('');
      return;
    }
    let cancelled = false;
    (async () => {
      setPlanLoading(true);
      setPlanError('');
      try {
        const r = await subscriptionPlanApi.getActive();
        const list = r?.data?.subscription_plans || r?.data?.plans || [];
        const found = list.find((p) => String(p.id) === String(planId));
        if (cancelled) return;
        if (!found) setPlanError('This subscription plan is not available.');
        else setPlan(found);
        try {
          const si = await subscriptionPlanApi.getStartIssues();
          const list = si?.data?.issues || [];
          if (!cancelled) {
            setStartIssues(Array.isArray(list) ? list : []);
            const current = (Array.isArray(list) ? list : []).find((i) => i.is_current) || list[0];
            if (current?.id) setStartMagazineIssueId(String(current.id));
          }
        } catch {
          if (!cancelled) setStartIssues([]);
        }
      } catch (e) {
        if (!cancelled) setPlanError(e?.message || 'Could not load this plan.');
      } finally {
        if (!cancelled) setPlanLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [planId]);

  const isPlan = Boolean(plan);
  const needsShip = isPlan ? Boolean(plan.includes_physical) : hasPhysical;
  const paySubtotal = isPlan ? (Number(plan.price_pkr) || 0) : subtotal;

  const [guest, setGuest] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_address: '',
    guest_city: '',
  });

  useEffect(() => {
    if (!needsShip) return undefined;
    let cancelled = false;
    (async () => {
      setCitiesLoading(true);
      setCitiesError('');
      try {
        const r = await shippingApi.getCities();
        const list = Array.isArray(r?.data?.cities) ? r.data.cities : [];
        if (!cancelled) {
          setShippingCities(list);
          if (list.length === 0) {
            setCitiesError(r?.data?.message || 'Courier city list is unavailable right now.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setShippingCities([]);
          setCitiesError(e?.response?.data?.message || e?.message || 'Could not load courier cities.');
        }
      } finally {
        if (!cancelled) setCitiesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [needsShip]);

  useEffect(() => {
    if (!user) return;
    setGuest((g) => ({
      guest_name: g.guest_name || user.name || '',
      guest_email: g.guest_email || user.email || '',
      guest_phone: g.guest_phone || user.phone || '',
      guest_address: g.guest_address || user.address || '',
      guest_city: g.guest_city || user.city || '',
    }));
  }, [user]);

  const filteredCities = useMemo(() => {
    const q = cityFilter.trim().toLowerCase();
    if (!q) return shippingCities;
    return shippingCities.filter((c) => String(c.name || '').toLowerCase().includes(q));
  }, [shippingCities, cityFilter]);

  useEffect(() => {
    if (!guest.guest_city || shippingCities.length === 0) return;
    const match = shippingCities.find(
      (c) => String(c.name).toLowerCase() === String(guest.guest_city).toLowerCase()
    );
    if (match && match.name !== guest.guest_city) {
      setGuest((g) => ({ ...g, guest_city: match.name }));
    }
  }, [shippingCities]); // eslint-disable-line react-hooks/exhaustive-deps

  const [payment_method, setPaymentMethod] = useState('JazzCash');
  const physicalCopyCount = useMemo(
    () => items
      .filter((i) => i.delivery_type === 'Physical')
      .reduce((n, i) => n + Math.max(1, i.quantity || 1), 0),
    [items],
  );
  /** Rs 100 first copy + Rs 50 each extra (not editable on website). */
  const delivery_charges_pkr = physicalDeliveryChargesPkr(physicalCopyCount);
  const [transaction_id, setTransactionId] = useState('');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [notes, setNotes] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Persist guest details so JazzCash fail → back to checkout does not require retyping.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CHECKOUT_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.guest && typeof draft.guest === 'object') {
        setGuest((g) => ({ ...g, ...draft.guest }));
        if (draft.guest.guest_city) setCityFilter(String(draft.guest.guest_city));
      }
      if (draft?.payment_method && PAYMENT_METHODS.some((p) => p.id === draft.payment_method && p.enabled)) {
        setPaymentMethod(draft.payment_method);
      }
      if (draft?.notes) setNotes(String(draft.notes));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify({
        guest,
        payment_method,
        notes,
      }));
    } catch {
      /* ignore */
    }
  }, [guest, payment_method, notes]);

  const payDelivery = isPlan || !needsShip ? 0 : delivery_charges_pkr;
  const payTotal = paySubtotal + payDelivery;

  const paymentMethod = useMemo(
    () => PAYMENT_METHODS.find(p => p.id === payment_method) || PAYMENT_METHODS[0],
    [payment_method],
  );

  const resolveCityFromFilter = (raw) => {
    const q = String(raw || '').trim().toLowerCase();
    if (!q) return null;
    return shippingCities.find((c) => String(c.name).toLowerCase() === q) || null;
  };

  // Validation per backend schema
  const validate = () => {
    const e = {};
    let city = guest.guest_city.trim();
    if (needsShip && !city) {
      const matched = resolveCityFromFilter(cityFilter);
      if (matched) city = matched.name;
    }

    if (!guest.guest_name.trim()) e.guest_name = 'Name is required.';
    else if (guest.guest_name.trim().length < 2) e.guest_name = 'Name must be at least 2 characters.';

    if (!guest.guest_email.trim()) e.guest_email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.guest_email.trim()))
      e.guest_email = 'Enter a valid email address.';

    if (!guest.guest_phone.trim()) e.guest_phone = 'Phone number is required.';
    else if (guest.guest_phone.replace(/[^0-9]/g, '').length < 10)
      e.guest_phone = 'Phone number must be at least 10 digits.';

    if (needsShip) {
      if (!guest.guest_address.trim()) e.guest_address = 'Shipping address is required for physical delivery.';
      else if (guest.guest_address.trim().length < 10) e.guest_address = 'Address is too short.';
      if (!city) e.guest_city = 'Please search and select a city.';
      else if (shippingCities.length > 0
        && !shippingCities.some((c) => String(c.name).toLowerCase() === city.toLowerCase())) {
        e.guest_city = 'Please select a city from the list.';
      } else if (city !== guest.guest_city) {
        setGuest((g) => ({ ...g, guest_city: city }));
        setCityFilter(city);
      }
    }

    if (isPlan && !startMagazineIssueId) e.start_magazine_issue_id = 'Choose which month your first magazine should be.';
    if (!isPlan && items.length === 0) e.cart = 'Your cart is empty — add magazines first.';
    if (!acceptTerms) e.acceptTerms = 'You must accept terms to place your order.';
    if (payment_method === 'BankTransfer' && !transaction_id.trim()) {
      e.transaction_id = 'Please enter your bank transfer reference / transaction ID.';
    }
    if (payment_method === 'BankTransfer' && !paymentScreenshot) {
      e.payment_screenshot = 'Please upload a payment screenshot (JPG, PNG, or PDF).';
    } else if (paymentScreenshot && paymentScreenshot.size > 5 * 1024 * 1024) {
      e.payment_screenshot = 'Screenshot must be 5 MB or smaller.';
    }

    setErrors(e);
    if (Object.keys(e).length > 0) {
      window.setTimeout(() => {
        const el = document.querySelector('[data-checkout-error="1"]');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
    return Object.keys(e).length === 0;
  };

  const setField = (k, v) => setGuest(g => ({ ...g, [k]: v }));

  const onSubmit = async (ev) => {
    if (ev?.preventDefault) ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const base = {
        guest_name: guest.guest_name.trim(),
        guest_email: guest.guest_email.trim(),
        guest_phone: guest.guest_phone.trim(),
        ...(needsShip ? {
          guest_address: guest.guest_address.trim(),
          guest_city: guest.guest_city.trim(),
        } : {}),
        payment_method,
        ...(transaction_id.trim() ? { transaction_id: transaction_id.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };

      if (isPlan) {
        const r = await subscriptionPlanApi.checkout(buildCheckoutFormData({
          ...base,
          subscription_plan_id: plan.id,
          start_magazine_issue_id: Number(startMagazineIssueId),
        }, isJazzCashGateway(payment_method) ? null : paymentScreenshot));
        const body = r?.data || {};
        if (body.reused) {
          toast.info(`Using your existing order ${body.order_number}. A second plan was not created.`);
        }
        const orderNumber = body.order_number || body.single_issue_purchase?.order_number || '';
        if (isJazzCashGateway(payment_method)) {
          if (!orderNumber) {
            toast.error('Order was created but JazzCash could not start. Please try again.');
            return;
          }
          // Keep cart until JazzCash confirms payment (fail → back to checkout, no re-shop).
          await redirectToJazzCash([orderNumber]);
          return;
        }
        if (!orderNumber) {
          toast.error('Order number missing. Please contact support.');
          return;
        }
        navigate(`/order/${encodeURIComponent(orderNumber)}`, {
          state: {
            order: {
              order_number: orderNumber,
              payment_status: 'Pending',
              order_status: 'Pending',
              delivery_type: needsShip ? 'Physical' : 'Digital',
              plan_name: plan.name,
            },
            payment_method,
            total_amount_pkr: payTotal,
            delivery_charges_pkr: 0,
            delivery_type: needsShip ? 'Physical' : 'Digital',
          },
        });
        return;
      }

      let lastOrder = null;
      const placedNumbers = [];
      const errors = [];
      let physicalDeliveryApplied = false;

      for (const it of items) {
        const isPhysical = it.delivery_type === 'Physical';
        const lineDelivery = isPhysical && !physicalDeliveryApplied ? delivery_charges_pkr : 0;
        if (isPhysical) physicalDeliveryApplied = true;
        const payload = buildCheckoutFormData({
          ...base,
          magazine_issue_id: it.issueId,
          quantity: Math.max(1, it.quantity || 1),
          delivery_type: it.delivery_type,
          delivery_charges_pkr: lineDelivery,
        }, isJazzCashGateway(payment_method) ? null : paymentScreenshot);
        try {
          const r = await ordersApi.storeGuestOrder(payload);
          const body = r?.data || {};
          if (body.reused) {
            toast.info(`Using existing order ${body.order_number || body.single_issue_purchase?.order_number}.`);
          }
          lastOrder = body.single_issue_purchase || body || null;
          const no = lastOrder?.order_number || body.order_number;
          if (no) placedNumbers.push(no);
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || 'Unknown error';
          const existingNo = err?.response?.data?.order_number;
          if (existingNo) {
            // Duplicate / already-paid magazine — show that order (not a JazzCash redirect case).
            toast.error(msg);
            navigate(`/order/${encodeURIComponent(existingNo)}`);
            return;
          }
          const details = err?.response?.data?.errors ? JSON.stringify(err.response.data.errors) : '';
          errors.push(`${it.snapshot?.title || it.snapshot?.month_year || `Issue #${it.issueId}`}: ${msg} ${details}`.trim());
        }
      }

      if (errors.length > 0) {
        toast.error(`We could not place ${errors.length} item${errors.length===1?'':'s'}: ${errors[0]}`);
      }
      if (isJazzCashGateway(payment_method)) {
        if (placedNumbers.length === 0) {
          if (!errors.length) toast.error('Could not start JazzCash payment. Please try again.');
          return;
        }
        // Never show thank-you until JazzCash return/IPN confirms payment.
        await redirectToJazzCash(placedNumbers);
        return;
      }
      if (lastOrder) {
        const orderNumber = lastOrder.order_number || lastOrder.id;
        if (!orderNumber) {
          toast.error('Order number missing. Please contact support.');
          return;
        }
        clearCart();
        navigate(`/order/${encodeURIComponent(orderNumber)}`, {
          state: {
            order: lastOrder,
            payment_method,
            multi_item_errors: errors,
            total_amount_pkr: payTotal,
            delivery_charges_pkr: delivery_charges_pkr,
            delivery_type: lastOrder.delivery_type || (needsShip ? 'Physical' : 'Digital'),
          },
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.';
      const alreadyPaid = !!(err?.alreadyPaid || err?.response?.data?.already_paid);
      const existingNo = err?.orderNumber || err?.response?.data?.order_number;
      if (alreadyPaid && existingNo) {
        toast.success(msg || 'Payment already confirmed.');
        navigate(`/order/${encodeURIComponent(existingNo)}?paid=1`);
        return;
      }
      // JazzCash prepare/redirect failed: cancel unpaid draft, stay on checkout (cart kept).
      if (isJazzCashGateway(payment_method)) {
        const nums = Array.isArray(err?.orderNumbers) ? err.orderNumbers : [];
        if (nums.length > 0) {
          try {
            await jazzcashApi.abandon(nums, 'JazzCash prepare/redirect failed');
          } catch {
            /* ignore */
          }
        }
        toast.error(msg);
        return;
      }
      toast.error(msg);
      if (existingNo) {
        navigate(`/order/${encodeURIComponent(existingNo)}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (planId && planLoading) {
    return (
      <ShopLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center text-gray-600 font-semibold">Loading plan…</div>
      </ShopLayout>
    );
  }

  if (planId && !plan) {
    return (
      <ShopLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-black text-gray-900 mb-3">Plan unavailable</h1>
          <p className="text-gray-600 mb-8">{planError || 'Choose another subscription plan.'}</p>
          <Link to="/plans" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold shadow-md" style={{ backgroundColor: FLARE_RED }}>
            View Plans
          </Link>
        </div>
      </ShopLayout>
    );
  }

  if (!isPlan && items.length === 0) {
    return (
      <ShopLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-black text-gray-900 mb-3">Your cart is empty</h1>
          <p className="text-gray-600 mb-8">Add magazines before checking out.</p>
          <Link
            to="/magazines"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold shadow-md"
            style={{ backgroundColor: FLARE_RED }}
          >
            Browse Magazines
          </Link>
        </div>
      </ShopLayout>
    );
  }

  const inputBase = 'mt-1.5 w-full px-4 py-3 rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 text-gray-900 text-sm font-medium transition-colors';
  const errMsg = (key) => errors[key]
    ? <p className="mt-1 text-xs font-semibold text-rose-600" data-checkout-error="1">{errors[key]}</p>
    : null;

  return (
    <ShopLayout>
      <form onSubmit={onSubmit} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="mb-8">
          <Link to={isPlan ? '/plans' : '/cart'} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
            <Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" className="h-4 w-4" />
            {isPlan ? 'Back to plans' : 'Back to cart'}
          </Link>
          <h1 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight text-gray-900">{isPlan ? 'Subscribe' : 'Checkout'}</h1>
          <p className="mt-1 text-gray-600">{isPlan ? `You are subscribing to ${plan.name}. Choose your first magazine month, enter your details, and pay. Delivery starts after we verify payment.` : 'Almost there — enter your details below and choose a payment method to complete your order.'}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 space-y-6">
            <section className="rounded-3xl bg-white border border-gray-100 shadow-sm p-6 lg:p-7">
              <h2 className="text-lg font-extrabold text-gray-900 mb-5 flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-sm font-black" style={{ backgroundColor: FLARE_RED }}>1</span>
                Your Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-sm font-bold text-gray-800">Full name *</label>
                  <input
                    value={guest.guest_name}
                    onChange={e => setField('guest_name', e.target.value)}
                    placeholder="e.g. Muhammad Ali"
                    className={`${inputBase} ${errors.guest_name ? 'border-rose-300 focus:ring-rose-200 focus:border-rose-400' : 'border-gray-200 focus:ring-rose-200 focus:border-rose-400'}`}
                  />
                  {errMsg('guest_name')}
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-800">Email address *</label>
                  <input
                    type="email"
                    value={guest.guest_email}
                    onChange={e => setField('guest_email', e.target.value)}
                    placeholder="you@example.com"
                    className={`${inputBase} ${errors.guest_email ? 'border-rose-300 focus:ring-rose-200' : 'border-gray-200 focus:ring-rose-200 focus:border-rose-400'}`}
                  />
                  {errMsg('guest_email')}
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-800">Mobile number *</label>
                  <input
                    type="tel"
                    value={guest.guest_phone}
                    onChange={e => setField('guest_phone', e.target.value)}
                    placeholder="0300 1234567"
                    className={`${inputBase} ${errors.guest_phone ? 'border-rose-300 focus:ring-rose-200' : 'border-gray-200 focus:ring-rose-200 focus:border-rose-400'}`}
                  />
                  {errMsg('guest_phone')}
                </div>

                {needsShip && (
                  <>
                    <div className="sm:col-span-2 mt-3">
                      <div className="text-xs font-bold uppercase tracking-widest mb-2 px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 inline-flex items-center gap-1.5">
                        <Icon path="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" className="h-3.5 w-3.5" />
                        Printed copy · Shipping address required
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-sm font-bold text-gray-800">Street address *</label>
                      <textarea
                        value={guest.guest_address}
                        onChange={e => setField('guest_address', e.target.value)}
                        rows={3}
                        placeholder="House / Flat #, Street, Area"
                        className={`${inputBase} resize-y ${errors.guest_address ? 'border-rose-300 focus:ring-rose-200' : 'border-gray-200 focus:ring-rose-200 focus:border-rose-400'}`}
                      />
                      {errMsg('guest_address')}
                    </div>
                    <div className="relative">
                      <label className="text-sm font-bold text-gray-800">City *</label>
                      <div className="relative">
                        <input
                          type="text"
                          autoComplete="address-level2"
                          value={cityMenuOpen ? cityFilter : (guest.guest_city || cityFilter)}
                          onChange={(e) => {
                            setCityFilter(e.target.value);
                            setField('guest_city', '');
                            setCityMenuOpen(true);
                            setErrors((prev) => {
                              if (!prev.guest_city) return prev;
                              const next = { ...prev };
                              delete next.guest_city;
                              return next;
                            });
                          }}
                          onFocus={() => {
                            setCityFilter(guest.guest_city || cityFilter);
                            setCityMenuOpen(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && filteredCities[0]) {
                              e.preventDefault();
                              const pick = filteredCities[0];
                              setField('guest_city', pick.name);
                              setCityFilter(pick.name);
                              setCityMenuOpen(false);
                            } else if (e.key === 'Escape') {
                              setCityMenuOpen(false);
                            }
                          }}
                          onBlur={() => {
                            window.setTimeout(() => {
                              setCityMenuOpen(false);
                              setGuest((g) => {
                                if (g.guest_city) return g;
                                const match = resolveCityFromFilter(cityFilter);
                                if (!match) return g;
                                setCityFilter(match.name);
                                return { ...g, guest_city: match.name };
                              });
                            }, 150);
                          }}
                          placeholder={citiesLoading ? 'Loading cities…' : 'Search & select city'}
                          disabled={citiesLoading || shippingCities.length === 0}
                          className={`${inputBase} pr-10 ${errors.guest_city ? 'border-rose-300 focus:ring-rose-200' : 'border-gray-200 focus:ring-rose-200 focus:border-rose-400'}`}
                        />
                        {(guest.guest_city || cityFilter) && !citiesLoading && (
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-gray-400 hover:text-gray-700 text-lg leading-none"
                            aria-label="Clear city"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setField('guest_city', '');
                              setCityFilter('');
                              setCityMenuOpen(true);
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      {cityMenuOpen && !citiesLoading && shippingCities.length > 0 && (
                        <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg py-1">
                          {filteredCities.length === 0 ? (
                            <li className="px-4 py-2.5 text-sm text-gray-500">No matching city</li>
                          ) : (
                            filteredCities.slice(0, 100).map((c) => (
                              <li key={c.id || c.name}>
                                <button
                                  type="button"
                                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-rose-50 ${
                                    guest.guest_city === c.name ? 'bg-rose-50 font-bold text-gray-900' : 'text-gray-800'
                                  }`}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setField('guest_city', c.name);
                                    setCityFilter(c.name);
                                    setCityMenuOpen(false);
                                    setErrors((prev) => {
                                      if (!prev.guest_city) return prev;
                                      const next = { ...prev };
                                      delete next.guest_city;
                                      return next;
                                    });
                                  }}
                                >
                                  {c.name}
                                </button>
                              </li>
                            ))
                          )}
                        </ul>
                      )}
                      {citiesError && (
                        <p className="mt-1 text-xs font-semibold text-amber-700">{citiesError}</p>
                      )}
                      {errMsg('guest_city')}
                    </div>
                  </>
                )}

                {isPlan && (
                  <div className="sm:col-span-2 mt-2">
                    <label className="text-sm font-bold text-gray-800">First magazine month *</label>
                    <p className="text-xs text-gray-500 mt-1 mb-2">Your plan covers {Number(plan.duration_months) || 1} consecutive {Number(plan.duration_months) === 1 ? 'issue' : 'issues'} starting from this month.</p>
                    <select
                      value={startMagazineIssueId}
                      onChange={(e) => setStartMagazineIssueId(e.target.value)}
                      className={`${inputBase} ${errors.start_magazine_issue_id ? 'border-rose-300 focus:ring-rose-200' : 'border-gray-200 focus:ring-rose-200 focus:border-rose-400'}`}
                    >
                      <option value="">Select first magazine</option>
                      {startIssues.map((issue) => (
                        <option key={issue.id} value={issue.id}>
                          {(issue.month_year || issue.title || `Issue #${issue.id}`)}{issue.is_current ? ' · current' : ' · next'}
                        </option>
                      ))}
                    </select>
                    {errors.start_magazine_issue_id && <p className="mt-1 text-xs font-semibold text-rose-600">{errors.start_magazine_issue_id}</p>}
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-3xl bg-white border border-gray-100 shadow-sm p-6 lg:p-7">
              <h2 className="text-lg font-extrabold text-gray-900 mb-5 flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-sm font-black" style={{ backgroundColor: FLARE_RED }}>2</span>
                Payment Method
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PAYMENT_METHODS.map(pm => {
                  const disabled = !pm.enabled;
                  return (
                    <label
                      key={pm.id}
                      className={`relative rounded-2xl border-2 p-4 transition-all ${
                        disabled
                          ? 'opacity-55 cursor-not-allowed bg-gray-50 border-gray-100'
                          : payment_method === pm.id
                            ? 'cursor-pointer border-gray-900 bg-white shadow-sm'
                            : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value={pm.id}
                        disabled={disabled}
                        checked={payment_method === pm.id}
                        onChange={() => !disabled && setPaymentMethod(pm.id)}
                        className="sr-only"
                      />
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${pm.accent} flex items-center justify-center shrink-0`}>
                          <Icon path={pm.iconPath} className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-extrabold text-gray-900 flex items-center gap-2 flex-wrap">
                            {pm.label}
                            {disabled && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                                Coming soon
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{pm.desc}</div>
                        </div>
                        {!disabled && (
                          <div className={`w-5 h-5 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center ${payment_method === pm.id ? 'border-gray-900' : 'border-gray-300'}`}>
                            {payment_method === pm.id && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: FLARE_RED }} />}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {isJazzCashGateway(payment_method) && (
                <div className="mt-5 p-4 rounded-2xl bg-gradient-to-br from-rose-50 to-white border border-rose-100">
                  <div className="text-xs font-bold uppercase tracking-widest mb-2 text-rose-700">
                    Secure JazzCash checkout
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    You will be redirected to JazzCash to pay{' '}
                    <span className="font-black" style={{ color: FLARE_RED }}>{formatPKR(payTotal)}</span>
                    {payment_method === 'Online' ? ' by card' : ' via JazzCash wallet / voucher / card'}.
                    After payment you return to Flare automatically.
                  </p>
                </div>
              )}

              {payment_method === 'BankTransfer' && (
                <>
                  <McbTransferCard amount={payTotal} />
                  <div className="mt-5 p-4 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200">
                    <div className="text-xs font-bold uppercase tracking-widest mb-2 text-gray-500">
                      Pay &amp; enter reference
                    </div>
                    <div className="text-sm text-gray-700 space-y-1.5 mb-4 leading-relaxed">
                      Transfer <span className="font-black" style={{ color: FLARE_RED }}>{formatPKR(payTotal)}</span> to{' '}
                      <strong>MCB</strong> account title <strong>FLARE</strong> (
                      <code className="mx-1 px-1.5 py-0.5 rounded-md bg-white border font-bold">{MCB_ACCOUNT.number}</code>
                      ), then enter your bank reference and upload the payment screenshot below.
                    </div>
                    <label className="text-sm font-bold text-gray-800">
                      Bank transfer reference / transaction ID *
                    </label>
                    <input
                      value={transaction_id}
                      onChange={e => setTransactionId(e.target.value)}
                      placeholder="e.g. bank reference / deposit slip #"
                      className={`${inputBase} ${errors.transaction_id ? 'border-rose-300 focus:ring-rose-200' : 'border-gray-200 focus:ring-rose-200 focus:border-rose-400'}`}
                    />
                    {errMsg('transaction_id')}

                    <div className="mt-4 relative overflow-hidden rounded-xl border border-dashed border-gray-300 bg-white p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="text-xs font-black uppercase tracking-wider text-gray-500">Payment screenshot *</div>
                          <p className="mt-1 text-sm text-gray-600">Upload proof of payment (JPG / PNG / PDF, max 5 MB).</p>
                        </div>
                        {paymentScreenshot && (
                          <button
                            type="button"
                            className="text-xs font-bold text-rose-600 hover:underline"
                            onClick={() => setPaymentScreenshot(null)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <label className={`mt-3 flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl border text-sm font-semibold cursor-pointer transition-colors ${
                        errors.payment_screenshot
                          ? 'border-rose-300 bg-rose-50 text-rose-700'
                          : paymentScreenshot
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-white'
                      }`}>
                        <Icon path="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" className="h-4 w-4" />
                        {paymentScreenshot ? paymentScreenshot.name : 'Choose file'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setPaymentScreenshot(file);
                            if (file) setErrors((prev) => {
                              const next = { ...prev };
                              delete next.payment_screenshot;
                              return next;
                            });
                          }}
                        />
                      </label>
                      {errMsg('payment_screenshot')}
                    </div>
                  </div>
                </>
              )}

              <div className="mt-5">
                <label className="text-sm font-bold text-gray-800">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Anything we should know? (Preferred courier, delivery instructions, etc.)"
                  className={`${inputBase} border-gray-200 focus:ring-rose-200 focus:border-rose-400 resize-y`}
                />
              </div>
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 space-y-5">
            <div className="rounded-3xl bg-white border border-gray-100 shadow-sm p-6">
              <h2 className="text-lg font-extrabold text-gray-900 mb-5 flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-sm font-black" style={{ backgroundColor: FLARE_RED }}>3</span>
                Review
              </h2>

              <div className="space-y-3 max-h-72 overflow-auto pr-1 -mr-1 pb-1">
                {isPlan ? (
                  <div className="flex gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="inline-block text-[9px] font-bold uppercase tracking-wider mb-1 px-1.5 py-0.5 rounded-md bg-rose-50 text-red-700">
                        Subscription · {needsShip ? 'Printed' : 'Digital'}
                      </div>
                      <div className="text-sm font-bold text-gray-900 leading-tight">{plan.name}</div>
                      <div className="text-[11px] text-gray-500">{plan.duration_months} month{Number(plan.duration_months) === 1 ? '' : 's'} · doorstep issues included</div>
                    </div>
                    <div className="text-sm font-black" style={{ color: FLARE_RED }}>{formatPKR(paySubtotal)}</div>
                  </div>
                ) : items.map(it => {
                  const snap = it.snapshot || {};
                  return (
                    <div key={it.cartId} className="flex gap-3">
                      <div className="w-12 shrink-0 aspect-[3/4] rounded-lg bg-gray-50 border border-gray-100 overflow-hidden">
                        {snap.cover_image_url && <img src={snap.cover_image_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className={`inline-block text-[9px] font-bold uppercase tracking-wider mb-1 px-1.5 py-0.5 rounded-md ${it.delivery_type === 'Digital' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-800'}`}>
                              {it.delivery_type}
                            </div>
                            <div className="text-sm font-bold text-gray-900 leading-tight line-clamp-2">{snap.title || snap.month_year || 'Issue'}</div>
                            <div className="text-[11px] text-gray-500">Qty {it.quantity || 1} × {formatPKR(unitPrice(it))}</div>
                          </div>
                          <div className="text-sm font-black" style={{ color: FLARE_RED }}>{formatPKR(lineTotal(it))}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-100 my-4" />

              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="font-semibold text-gray-600">Subtotal</dt>
                  <dd className="font-bold text-gray-900">{formatPKR(paySubtotal)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="font-semibold text-gray-600">
                    Delivery
                    {needsShip && !isPlan && physicalCopyCount > 0 ? (
                      <span className="block text-[11px] font-medium text-gray-400">
                        {physicalCopyCount === 1
                          ? '1 copy'
                          : `1st copy Rs 100 + ${physicalCopyCount - 1}× Rs 50`}
                      </span>
                    ) : null}
                  </dt>
                  <dd className="font-bold text-gray-900">{needsShip && !isPlan ? formatPKR(payDelivery) : 'Free'}</dd>
                </div>
                {needsShip && guest.guest_city ? (
                  <div className="flex items-center justify-between text-xs font-semibold text-gray-500">
                    <dt>City</dt>
                    <dd className="text-gray-800">{guest.guest_city}</dd>
                  </div>
                ) : null}
                <div className="flex items-center justify-between text-xs font-semibold text-gray-500">
                  <dt>Method</dt>
                  <dd>{paymentMethod?.label}</dd>
                </div>
                <div className="border-t border-gray-100 my-3" />
                <div className="flex items-start justify-between">
                  <dt className="font-extrabold text-gray-900 text-base">Total due</dt>
                  <dd className="text-right">
                    <div className="text-3xl font-black tracking-tight" style={{ color: FLARE_RED }}>{formatPKR(payTotal)}</div>
                    <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Pay via {paymentMethod?.label}</div>
                  </dd>
                </div>
              </dl>

              <label className="mt-5 flex items-start gap-2 text-xs text-gray-600 font-semibold cursor-pointer">
                <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300" style={{ accentColor: FLARE_RED }} />
                I agree to Flare Magazine's terms of sale, cancellation policy, and consent to share my info for delivery processing.
              </label>
              {errMsg('acceptTerms')}
              {errors.cart && <p className="mt-2 text-xs font-semibold text-rose-600" data-checkout-error="1">{errors.cart}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-extrabold shadow-lg transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                style={{ backgroundColor: FLARE_RED, boxShadow: '0 10px 24px -10px rgba(196,21,28,0.5)' }}
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-[3px] border-white/30 border-t-white animate-spin" />
                    Placing order…
                  </>
                ) : (
                  <>
                    Place Order · {formatPKR(payTotal)}
                    <Icon path="M5 13l4 4L19 7" className="h-5 w-5" />
                  </>
                )}
              </button>

              <p className="mt-4 text-center text-[11px] text-gray-400 font-semibold uppercase tracking-widest">
                Order confirmation email will be sent after review
              </p>
            </div>
          </aside>
        </div>
      </form>
    </ShopLayout>
  );
}
